import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/app.state";
import { CommonService } from "src/app/shared/common.service";
import * as quotationsActions from './quotations.actions'
import { catchError, filter, finalize, map, mergeMap, of, switchMap, tap, withLatestFrom } from "rxjs";
import { APIResponse } from "src/app/core/models/api-response";
import { IQuotation, IQuotationDetail, IQuotationsKPIs, IQuotationSourceContent, ISentToErpRespone, IOrderLineAlert, IQuotationAlertsResponse, IQuotationDealsResponse, IDocumentCoordinatesResponse } from "../models/quotations.models";
import { AlertEnums } from "src/app/shared/enums/shared.enums";
import { QuotesService } from "../services/quotations.services";
import { DEFAULT_LINES_PAGE_SIZE, DEFAULT_PAGE_SIZE, ERP_ERROR_LONG_THRESHOLD, TOAST_ERP_ERROR_DURATION, TOAST_ERROR_DURATION, TOAST_PERMANENT } from "src/app/config/global.config";
import { HttpErrorResponse } from "@angular/common/http";
import { AppError } from "src/app/core/models/api-error";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { FeatureFlagService } from "src/app/core/services/feature-flag.service";
import { ErrorMessageService } from "src/app/core/services/error-message.service";
import { durationFor, severityFor } from "src/app/core/http/error-presentation";
import { parseFilterRequest } from "src/app/shared/utils/helpers";

@Injectable()
export class QuotesEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private quotesService: QuotesService,
    private commonService: CommonService,
    private translateService: TranslateService,
    private router: Router,
    private featureFlagService: FeatureFlagService,
    private errorMessages: ErrorMessageService
  ) { }

  /**
   * The only response `approve_quotation` returns that carries ERP wording. Its six
   * other failures — not found, unauthorized, already-sent, syncing, empty order,
   * server fault — never reached the ERP, and framing those as ERP rejections sends
   * the user into their ERP to hunt for a problem that is not there.
   *
   * Matching the message is a stand-in for a backend code: when one is added
   * alongside the split-out `erp_message` field, key on that instead. Until then a
   * reworded prefix degrades to the plain toast rather than mislabelling anything.
   */
  private static readonly ERP_REJECTION_PREFIX = 'Could not send order to ERP:';

  private isErpRejection(error: AppError, detail: string): boolean {
    return error.status === 400 && detail.startsWith(QuotesEffects.ERP_REJECTION_PREFIX);
  }

  /**
   * The ERP's own wording is the actionable part of a failed push — it names the
   * field the BO user has to fix in their ERP. Show it inside a translated shell,
   * and give a long rejection longer to be read.
   *
   * Opting this request out of the global interceptor also opted it out of the
   * interceptor's severity and duration rules, so both are applied here.
   */
  private showValidateOrderError(error: AppError): void {
    const detail = Array.isArray(error.messages) ? error.messages.join('\n') : error.messages;

    if (!this.isErpRejection(error, detail)) {
      this.commonService.showToast(detail, severityFor(error), { duration: durationFor(error) });
      return;
    }

    this.commonService.showToast(detail, 'ERROR', {
      variant: 'ERP_PUSH_ERROR',
      variantTitle: this.translateService.instant(`alerts.${AlertEnums.quotationErpPushErrorTitle}`),
      duration: detail.length > ERP_ERROR_LONG_THRESHOLD
        ? TOAST_ERP_ERROR_DURATION
        : TOAST_ERROR_DURATION,
    });
  }


  // fetch All Quotations
  FetchAllQuotationsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotations),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.getQuotations(action.page, action.filter, action.key, action.sort_by, action.sort_mode, action.status, action.archived).pipe(
          map((res: APIResponse<IQuotationsKPIs>) => quotationsActions.GetQuotationsSuccess({ res })),
          catchError(() => of(
            quotationsActions.GetQuotationsError()
          )),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      ),
    )
  );


  // get Quotation By Id
  GetQuotationByIdEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotationById),
      tap((action) => {
        if (!action.skipGlobalLoader) {
          this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
        }
      }),
      switchMap((action) =>
        this.quotesService.getQuotationById(
          action.id,
          action.paginate_lines,
          action.page,
          action.limit,
          action.include_ai_details,
        ).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.GetQuotationByIdSuccess({ quotation: res.object!, append: action.append })),
          catchError(() => of(
            quotationsActions.GetQuotationByIdError()
          )),
          finalize(() => {
            if (!action.skipGlobalLoader) {
              this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
            }
          })
        )
      )
    )
  );

  // append next page of quotation products
  AppendQuotationPageEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.AppendQuotationPage),
      withLatestFrom(this.store.select((s) => s.quotations.linesMetadata)),
      filter(([_, meta]) => !!meta && meta.page < meta.pages),
      map(([action, meta]) => quotationsActions.GetQuotationById({
        id: action.id,
        paginate_lines: true,
        page: (meta!.page || 1) + 1,
        limit: meta!.limit || DEFAULT_LINES_PAGE_SIZE,
        append: true,
        skipGlobalLoader: true,
        include_ai_details: false,
      }))
    )
  );

  // update quotation details
  UpdateQuotationDetailsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.UpdateQuotationDetails),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.updateQuotationDetails(action.id, action.payload).pipe(
          map((res) => quotationsActions.UpdateQuotationDetailsSuccess({ changes: res.object!, orderId: action.id })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationUpdateSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(
            quotationsActions.UpdateQuotationDetailsError({ orderId: action.id })
          )),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // dismiss AI suggested shipping location
  DismissSuggestedLocationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.DismissSuggestedLocation),
      switchMap((action) =>
        this.quotesService.dismissSuggestedLocation(action.id).pipe(
          map(() => quotationsActions.DismissSuggestedLocationSuccess({ id: action.id })),
          catchError(() => of(
            quotationsActions.DismissSuggestedLocationError()
          ).pipe(
            tap(() => {
              this.commonService.showToast(
                this.translateService.instant(`alerts.${AlertEnums.quotationEditError}`),
                'ERROR'
              )
            })
          ))
        )
      )
    )
  );

  // duplicate badge dismissal
  DismissDuplicateBadgeEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.DismissDuplicateBadge),
      switchMap((action) =>
        this.quotesService.dismissDuplicateBadge(action.id).pipe(
          map(() => quotationsActions.DismissDuplicateBadgeSuccess({ id: action.id })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('quotations_and_orders.duplicate_dismissed_toast'),
              'SUCCESS'
            )
          }),
          catchError(() => of(
            quotationsActions.DismissDuplicateBadgeError()
          ).pipe(
            tap(() => {
              this.commonService.showToast(
                this.translateService.instant(`alerts.${AlertEnums.quotationEditError}`),
                'ERROR'
              )
            })
          ))
        )
      )
    )
  );

  // send Quotation
  SendQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.SendQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.sendQuotation(action.id).pipe(
          map((res: APIResponse<IQuotation>) => quotationsActions.SendQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            if(action.accept) {
              this.store.dispatch(quotationsActions.AcceptQuotation({ id: action.id }));
            } else {
              this.commonService.showToast(
                this.translateService.instant(`alerts.${AlertEnums.sendQuotationSuccess}`),
                'SUCCESS'
              );
            }
          }),
          catchError(() =>
            of(
              quotationsActions.SendQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );


  // Send new Quotation
  SendNewQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.SendNewQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.sendNewQuotation(action.newQuotation).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.SendNewQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.SendNewQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // Draft new Quotation
  DraftNewQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.DraftNewQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.draftNewQuotation(action.newQuotation).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.DraftNewQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationDraftAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.DraftNewQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // edit draft quotation
  EditDraftQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.EditDraftQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.editDraftQuotation(action.id, action.quotation).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.EditDraftQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationEditDraftSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.EditDraftQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // resend quotation

  // resend quotation
  ResendQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.ResendQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.resendQuotation(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.ResendQuotationSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.ResendQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // edit quotation
  EditQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.EditQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.editQuotation(action.id, action.quotation, action.update_status).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.EditQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationEditSuccess}`),
              'SUCCESS'
            );
          }),
          catchError((error) => of(quotationsActions.EditQuotationError())
            
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );


  // edit and send Quotation
  EditAndSendQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.EditAndSendQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.editAndSendQuotation(action.id, action.quotation).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.EditAndSendQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.EditAndSendQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  )

  // validate Quotation
  ValidateQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.validateOrder),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.validateOrder(action.id, action.shouldDelete, action.force, action.forceSend).pipe(
          map((res: APIResponse<ISentToErpRespone>) => quotationsActions.validateOrderSuccess({ quotation: res.object!, orderId: action.id })),
          tap((res) => {
            if(!res.quotation.erp_missing_products && !res.quotation.erp_missing_inventory) {
              this.commonService.showToast(
                this.translateService.instant(`alerts.${AlertEnums.quotationAprovedSuccess}`),
                'SUCCESS'
              );
            }
          }),
          catchError((httpError: HttpErrorResponse) => {
            const error = this.errorMessages.parse(httpError);
            this.showValidateOrderError(error);
            return of(quotationsActions.validateOrderError({ error }));
          }),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // cancel Quotation
  CancelQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.cancelQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.cancelQuotation(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.cancelQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationCancelSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.cancelQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );


  // invoice Quotation
  InvoiceQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.invoiceQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.invoiceQuotation(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.invoiceQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.invoiceQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  )
  // delete Quotation
  DeleteQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.deleteQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.deleteQuotation(action.id, action.confirm).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.deleteQuotationSuccess({ id: action.id })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationDeleteSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.deleteQuotationError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // duplicate Quotation
  DuplicateQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.DuplicateQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.duplicateQuotation(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.DuplicateQuotationSuccess({ quotation: res.object!, sourceId: action.id })),
          tap((successAction) => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationDuplicateSuccess}`),
              'SUCCESS'
            );
            if (successAction.quotation?.id) {
              this.router.navigate(['/quotations', successAction.quotation.id]);
            }
          }),
          catchError(() =>
            of(quotationsActions.DuplicateQuotationError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // bulk duplicate quotations (single API call, no navigation)
  BulkDuplicateQuotationsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.BulkDuplicateQuotations),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.bulkDuplicateQuotations(action.ids).pipe(
          map(() => quotationsActions.BulkDuplicateQuotationsSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('quotations_and_orders.bulk.duplicate_success'),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(quotationsActions.BulkDuplicateQuotationsError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // bulk archive quotations
  BulkArchiveQuotationsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.BulkArchiveQuotations),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.bulkArchiveQuotations(action.ids).pipe(
          map(() => quotationsActions.BulkArchiveQuotationsSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('quotations_and_orders.bulk.archive_success'),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(quotationsActions.BulkArchiveQuotationsError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // bulk unarchive quotations
  BulkUnarchiveQuotationsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.BulkUnarchiveQuotations),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.bulkUnarchiveQuotations(action.ids).pipe(
          map(() => quotationsActions.BulkUnarchiveQuotationsSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('quotations_and_orders.bulk.unarchive_success'),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(quotationsActions.BulkUnarchiveQuotationsError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // bulk delete quotations
  BulkDeleteQuotationsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.BulkDeleteQuotations),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.bulkDeleteQuotations(action.ids).pipe(
          map(() => quotationsActions.BulkDeleteQuotationsSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('quotations_and_orders.bulk.delete_success'),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(quotationsActions.BulkDeleteQuotationsError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // archive Quotation
  ArchiveQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.ArchiveQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.archiveQuotation(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) =>
            quotationsActions.ArchiveQuotationSuccess({
              id: action.id,
              quotation: res.object,
              quotationName: action.quotationName,
            })
          ),
          tap((successAction) => {
            const message = this.translateService.instant(
              `alerts.${AlertEnums.quotationArchiveSuccess}`,
              { name: successAction.quotationName ?? successAction.quotation?.name ?? '' }
            );
            const undoLabel = this.translateService.instant('quotations_and_orders.actions.undo');
            this.commonService.showToast(message, 'SUCCESS', {
              actionLabel: undoLabel,
              actionCallback: () => {
                this.store.dispatch(quotationsActions.UnarchiveQuotation({ id: action.id }));
              },
            });
          }),
          catchError(() =>
            of(quotationsActions.ArchiveQuotationError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // unarchive (restore) Quotation
  UnarchiveQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.UnarchiveQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      withLatestFrom(this.store.select((s) => s.quotations.filterState)),
      switchMap(([action, filterState]) =>
        this.quotesService.unarchiveQuotation(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) =>
            quotationsActions.UnarchiveQuotationSuccess({ id: action.id, quotation: res.object })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationRestoreSuccess}`),
              'SUCCESS'
            );
            this.store.dispatch(quotationsActions.GetQuotations({
              page: { number: 1, size: DEFAULT_PAGE_SIZE, order: 'desc', co_pilot: filterState.isCopilot ? 1 : 0},
              filter: parseFilterRequest('quotation', filterState.advanced) as any,
              key: filterState.keyword,
              archived: 1
            }));
          }),
          catchError(() =>
            of(quotationsActions.UnarchiveQuotationError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // update order status
  UpdateOrderStatusEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.UpdateOrderStatus),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.updateOrderStatus(action.id, action.status).pipe(
          map(() => quotationsActions.UpdateOrderStatusSuccess()),
          tap(() => {
            this.store.dispatch(quotationsActions.GetQuotationById({ id: action.parentId }));
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationAddSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.UpdateOrderStatusError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  )    // update payment status
  UpdatePaymentStatusEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.UpdatePaymentStatus),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.updatePaymentStatus(action.id, action.status).pipe(
          map(() => quotationsActions.UpdatePaymentStatusSuccess()),
          tap(() => {
            this.store.dispatch(quotationsActions.GetQuotationById({ id: action.parentId }));
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationUpdateSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() =>
            of(
              quotationsActions.UpdatePaymentStatusError()
            )
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  )
  // export
  exportQuotations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.exportQuotation),
      mergeMap((action) =>
        this.quotesService.exportQuotations(action.filter, action.key, action.co_pilot).pipe(
          map((file) => {
            const fileURL = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = `Quotations-${new Date().toLocaleString()}.xlsx`;
            link.click();
            return quotationsActions.exportQuotationSuccess({ file });
          }),
          catchError((error) =>
            of(quotationsActions.exportQuotationFailure({ error }))
          )
        )
      )
    )
  );

  // accept Quotation
  AcceptQuotationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.AcceptQuotation),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.acceptQuotation(action.id).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.AcceptQuotationSuccess({ quotation: res.object! })),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.acceptQuotationSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(quotationsActions.AcceptQuotationError())),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  )

  // get quotation source content
  GetQuotationSourceContentEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotationSourceContent),
      tap((action) => {
        if (!action.skipGlobalLoader) {
          this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
        }
      }),
      switchMap((action) => this.quotesService.getQuotationSourceContent(action.id).pipe(
        map((res: APIResponse<IQuotationSourceContent>) => quotationsActions.GetQuotationSourceContentSuccess({
          quotationId: action.id,
          quotation: res.object!,
        })),
        catchError(() => of(quotationsActions.GetQuotationSourceContentError())),
        finalize(() => {
          if (!action.skipGlobalLoader) {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          }
        })
      )),

    )
  );

  // get quotation document coordinates (WB-6815). Non-critical: never toggles the global loader
  // (the panel always passes skipGlobalLoader) and a failure just yields no highlights.
  GetQuotationDocumentCoordinatesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotationDocumentCoordinates),
      switchMap((action) => this.quotesService.getQuotationDocumentCoordinates(action.id).pipe(
        map((res: APIResponse<IDocumentCoordinatesResponse>) => quotationsActions.GetQuotationDocumentCoordinatesSuccess({
          quotationId: action.id,
          coordinates: res.object!,
        })),
        catchError(() => of(quotationsActions.GetQuotationDocumentCoordinatesError())),
      )),
    )
  );

  // update email order
  UpdateEmailOrderEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.UpdateEmailOrder),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) => this.quotesService.updateEmailOrder(action.id, action.payload).pipe(
        map((res: APIResponse<IQuotationDetail>) => quotationsActions.UpdateEmailOrderSuccess({ quotation: res.object! })),
        tap(() => {
          this.commonService.showToast(
            this.translateService.instant(`alerts.${AlertEnums.quotationUpdateSuccess}`),
            'SUCCESS'
          );
        }),
        catchError(() => of(quotationsActions.UpdateEmailOrderError())),
        finalize(() => {
          this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
        })
      )),
    )
  );

  // update ai notes
  UpdateAiNotesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.UpdateAiNotes),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) => this.quotesService.updateAiNotes(action.id, action.payload).pipe(
        map((res: APIResponse<IQuotationDetail>) => quotationsActions.UpdateAiNotesSuccess({ quotation: res.object! })),
        tap(() => {
          this.commonService.showToast(
            this.translateService.instant(`alerts.${AlertEnums.quotationUpdateSuccess}`),
            'SUCCESS'
          );
        }),
        catchError(() => of(quotationsActions.UpdateAiNotesError())),
        finalize(() => {
          this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
        })
      )),
    )
  );

  checkProductsAvailability$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(quotationsActions.CheckProductsAvailability),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.checkProductAvailability(action.data).pipe(
          map((resp) =>
            quotationsActions.CheckProductsAvailabilitySuccess({ data: resp.object })
          ),
          catchError(() =>
            of(quotationsActions.CheckProductsAvailabilityError())
          ),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    );
  });

  // delete duplicate products
  DeleteDuplicateProductsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.DeleteDuplicateProducts),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.deleteDuplicateProducts(action.id, action.payload).pipe(
          map((res: APIResponse<IQuotationDetail>) => quotationsActions.DeleteDuplicateProductsSuccess({ quotation: res.object! })),
          tap(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationUpdateSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
            return of(quotationsActions.DeleteDuplicateProductsError());
          })
        )
      )
    )
  );

  // update quotation lines
  UpdateQuotationLinesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.UpdateQuotationLines),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      withLatestFrom(
        this.store.select((s) => s.quotations.linesMetadata),
        this.store.select((s) => s.quotations.quotation),
      ),
      switchMap(([action, linesMeta, quotation]) => {
        const linesMetadataForPatch =
          quotation?.id === action.id && linesMeta != null ? linesMeta : null;
        return this.quotesService
          .updateQuotationLines(action.id, action.payload, linesMetadataForPatch)
          .pipe(
            map((res) => quotationsActions.UpdateQuotationLinesSuccess({ delta: res.object!, orderId: action.id })),
            tap(() => {
              this.commonService.showToast(
                this.translateService.instant(`alerts.${AlertEnums.quotationUpdateSuccess}`),
                'SUCCESS'
              );
            }),
            catchError(() => of(quotationsActions.UpdateQuotationLinesError())),
            finalize(() => {
              this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
            }),
          );
      }),
    )
  );

  // prompt to order
  PromptToOrderEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.PromptToOrder),
      tap(() => {
        this.store.dispatch(quotationsActions.SetPromptToOrderLoader({ isLoading: true }));
      }),
      switchMap((action) => this.quotesService.promptToOrder(action.id, action.payload).pipe(
        map((res: APIResponse<IQuotationDetail>) => quotationsActions.PromptToOrderSuccess({ quotation: res.object! })),
        catchError(() => of(quotationsActions.PromptToOrderError()).pipe(
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.quotationPromptToOrderError}`),
              'ERROR',
              {
                variant: 'PROMPT_TO_ORDER_ERROR',
                variantTitle: this.translateService.instant('quotations_and_orders.prompt.no_products_found'),
                duration: TOAST_PERMANENT,
              }
            );
          })
        )),
        finalize(() => {
          this.store.dispatch(quotationsActions.SetPromptToOrderLoader({ isLoading: false }));
        })
      )),
    )
  );

  // toggle order line alert (ignore/revert)
  ToggleOrderLineAlertEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.ToggleOrderLineAlert),
      tap(() => {
        this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.quotesService.toggleOrderLineAlert(action.quotationId, action.orderLineId, action.alertId).pipe(
          map((res: APIResponse<IOrderLineAlert>) =>
            quotationsActions.ToggleOrderLineAlertSuccess({ orderLineId: action.orderLineId, alert: res.object! })
          ),
          catchError(() => of(quotationsActions.ToggleOrderLineAlertError())),
          finalize(() => {
            this.store.dispatch(quotationsActions.SetQuotationsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // ignore all open alerts
  IgnoreAllAlertsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.IgnoreAllAlerts),
      switchMap((action) =>
        this.quotesService.ignoreAllAlerts(action.quotationId, action.alertIds).pipe(
          map(() =>
            quotationsActions.IgnoreAllAlertsSuccess({ alertIds: action.alertIds })
          ),
          catchError(() => of(quotationsActions.IgnoreAllAlertsError()))
        )
      )
    )
  );

  // load quotation alerts from dedicated endpoint after quotation loads
  LoadQuotationAlertsOnDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotationByIdSuccess),
      filter((action) => !action.append),
      map((action) => quotationsActions.LoadQuotationAlerts({ quotationId: action.quotation.id }))
    )
  );

  LoadQuotationAlertsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.LoadQuotationAlerts),
      switchMap((action) =>
        this.quotesService.getQuotationAlerts(action.quotationId).pipe(
          map((res: APIResponse<IQuotationAlertsResponse>) =>
            quotationsActions.LoadQuotationAlertsSuccess({ alertsResponse: res.object ?? { alerts_by_line: {}, has_any_open: false } })
          ),
          catchError(() => of(quotationsActions.LoadQuotationAlertsError()))
        )
      )
    )
  );

  // lazy-load full history after quotation detail loads
  LoadQuotationHistoryOnDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotationByIdSuccess),
      filter((action) => !action.append),
      map((action) => quotationsActions.LoadQuotationHistory({ quotationId: action.quotation.id }))
    )
  );

  LoadQuotationHistoryEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.LoadQuotationHistory),
      switchMap((action) =>
        this.quotesService.getQuotationHistory(action.quotationId).pipe(
          map((res) =>
            quotationsActions.LoadQuotationHistorySuccess({ history: res.data ?? [] })
          ),
          catchError(() => of(quotationsActions.LoadQuotationHistoryError()))
        )
      )
    )
  );

  LoadQuotationDealsOnDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.GetQuotationByIdSuccess),
      filter((action) => !action.append && this.featureFlagService.loyaltyEnabled),
      map((action) => quotationsActions.LoadQuotationDeals({ quotationId: action.quotation.id }))
    )
  );

  LoadQuotationDealsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quotationsActions.LoadQuotationDeals),
      switchMap((action) =>
        this.quotesService.getQuotationDeals(action.quotationId).pipe(
          map((res: APIResponse<IQuotationDealsResponse>) =>
            quotationsActions.LoadQuotationDealsSuccess({ dealsResponse: res.object ?? { order_deals: { best_deal: [], best_free_shipping: null, best_buy_x_get_y: null, order_programs: [] }, deals_by_line: {} } })
          ),
          catchError(() => of(quotationsActions.LoadQuotationDealsError()))
        )
      )
    )
  );

  // Best-effort: tell the backend the order was opened. The reducer already
  // cleared the unread flag optimistically, so a failure here is non-blocking.
  MarkQuotationReadEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(quotationsActions.MarkQuotationRead),
        mergeMap((action) =>
          this.quotesService.markQuotationRead(action.id).pipe(catchError(() => of(null)))
        )
      ),
    { dispatch: false }
  );
}
